/* eslint-disable no-param-reassign */
import {
  Bitmap,
  CanvasShader,
  Frame,
  ObjectId,
  KeyboardVisibilityEvent,
  Point,
  Rect,
  Touches,
  View,
  Canvas,
  BitmapType,
} from './view';
import { Resources } from './resources';
import { CanvasDecoder } from './canvasdecoder';
import {
  FrameBundleRootProto,
  FrameRootProto,
  FSFBViewProto,
  ViewStateProto,
  ViewProto,
  CanvasTokenTypeObject,
  LegacyPath,
  FSFBImageType,
  FSFBWebEvent,
  FSFBWebEventLoad,
  FSFBWebEventLoadNotRecorded,
  FSFBWebEventBundle,
  RectStruct,
  FSEvent,
  EventKeyboardVisibility,
  WebBundleProto,
} from '@fullstory/mobile-flatbuffers';
import { NinePatchChunkParser } from './android/9patch';
import { MobileDriver } from './driver';
import { SelectorFactory } from './selector_factory';
import { CanvasTokenDefs, CanvasTokenDef } from './canvastokendef';
import { PageTypeEnum } from '@fullstory/client-api';
import { loadEvent } from '@fullstory/util/promise';
import { WebBundlesBuilder } from './webbundle';
import { MobileTypeEnum } from './loader/pagedata_mobiletype';

const FSFBWebEventUnload_OFFSET = 3000;

export class MobileData {
  // An array of frames from a bundle.
  frames: { [index: number]: Frame };
  // The timestamp of the last frame within the bundle. This may be greater than the timestamp of the last frame in `frames` due to the fact that frames without
  // any view content (idle frames, or duplicate frames) are not returned there.
  lastFrameTime: number;
}

export class DataUnpack<OP> {
  private protocolVersion: number;
  private frames: { [index: number]: Frame } = {};
  private driver: MobileDriver<OP>;
  private tokenDefs: CanvasTokenDefs;

  private canvas: Uint8Array;
  private strings: { [id: number]: string } = {};
  private paths: { [id: number]: Path2D } = {};
  private shaders: { [id: number]: CanvasShader } = {};
  private bitmaps: { [id: number]: Bitmap } = {};
  private viewCache: { [offset: number]: View } = {};
  private selectorFactory: SelectorFactory;

  private resourceData: Resources;
  // The timestamp of the last frame we've unpacked.
  private lastFrameTime: number = 0;

  private static retaggedBlobURLs: Map<string, string> = new Map();

  // Not intended to be called by the public
  constructor(frameRoot: FrameBundleRootProto, driver: MobileDriver<OP>, resourceData: Resources) {
    this.protocolVersion = frameRoot.protocolVersion();
    this.resourceData = resourceData;
    this.driver = driver;
    this.selectorFactory = new SelectorFactory(frameRoot);
    this.strings = this.unpackStringTable(frameRoot);
    this.canvas = this.unpackCanvasBundle(frameRoot);
    this.tokenDefs = new CanvasTokenDefs(
      frameRoot.canvasDefinitionArray() || driver.defaultCanvasDefintion,
    );

    // lastFrame can be null in the case where we have an unload bundle with events
    const lastFrame = frameRoot.lastFrame();
    this.forEachList(lastFrame, x => this.unpackFrameObjectTables(x));

    const frames = this.unpackList(lastFrame, (x: FrameRootProto) =>
      this.unpackFrame(x, frameRoot),
    );
    this.frames = [];

    // Clear the view cache
    this.viewCache = {};
    for (let i = 0; i < frames.length; i++) {
      const frame = frames[i];
      // Frames can be null in the case where we have frame-less bundles (ie: just events)
      if (frame) {
        const index = frame.index;
        this.frames[index] = frame;
      }
    }
  }

  /**
   * Unpacks a flatbuffer object to a strongly-typed RootView.
   */
  static unpack<OP>(
    rootFb: FrameBundleRootProto,
    driver: MobileDriver<OP>,
    resourceData: Resources,
  ): MobileData {
    const unpacker = new DataUnpack<OP>(rootFb, driver, resourceData);
    return { frames: unpacker.frames, lastFrameTime: unpacker.lastFrameTime };
  }

  static unpackFrameBitmaps(
    resourceData: Resources,
    frameRoot: any,
    isIOS: boolean,
    protocolVersion: number,
  ): { [imageId: number]: Bitmap } {
    return DataUnpack.unpackBitmaps(resourceData, true, frameRoot, isIOS, protocolVersion);
  }

  static unpackWebBundles(
    root: FrameBundleRootProto,
    webBundles: WebBundlesBuilder,
    os: MobileTypeEnum,
  ) {
    let l = root.webEventsLength();

    // TODO(mmastrac): Due to a shared library bug, we can't trust the timestamps of stuff sent earlier than 1570146345
    // This can probably go away >= Dec 2019
    const improviseTimestamps = root.protocolVersion() < 1570146345;
    if (improviseTimestamps) {
      l = 0;
      webBundles.improviseTimestamps();
    }

    for (let i = 0; i < l; i++) {
      const webEvent = root.webEvents(i);
      const id = DataUnpack.unpackObjectId(0, webEvent.webViewId64());
      const epoch = webEvent.webViewEpoch();
      const type = webEvent.eventType();
      const when = webEvent.sessionTimestampMs();

      const bundle = webBundles.findOrCreate(id, epoch);

      switch (type) {
        case FSFBWebEvent.FSFBWebEventLoad: {
          const event = webEvent.event(new FSFBWebEventLoad());
          bundle.setStart(when);
          bundle.setUrl(event.url(), true);
          break;
        }
        case FSFBWebEvent.FSFBWebEventLoadNotRecorded: {
          const event = webEvent.event(new FSFBWebEventLoadNotRecorded());
          bundle.setNotRecordedReason(event.reason());
          bundle.setUrl(event.domain() || 'unknown', false);
          bundle.setStart(when);

          // Android sends a single FSFBWebEventLoadNotRecorded event, unlike iOS which sends on every frame.
          // Therefore we break early since we cannot derive an accurate end time from the last event.
          if (os === MobileTypeEnum.Android) {
            break;
          }

          // TODO(ryanwang): iOS currently does not send a corresponding FSFBWebEventUnload event to denote the end
          // of a web bundle. Therefore all FSFBWebEventLoadNotRecorded bundles have an _end of `Infinity`, and its
          // web-view-container would never get destroyed. Since we're sending FSFBWebEventLoadNotRecorded on every frame,
          // we can add an offset to each event, which results in the last event determining the _end time.
          // Add a protocolVersion check when we send a FSFBWebEventUnload event for their FSFBWebEventLoadNotRecorded events.
          bundle.setEnd(when + FSFBWebEventUnload_OFFSET);
          break;
        }
        case FSFBWebEvent.FSFBWebEventUnload: {
          // No args in the event we care about
          bundle.setEnd(when);
          break;
        }
        case FSFBWebEvent.FSFBWebEventBundle: {
          const event = webEvent.event(new FSFBWebEventBundle());
          bundle.addEvents(when, event);
          break;
        }
      }
    }
    let webBundle = root.lastWebBundle();
    if (webBundle) this.unpackWebEvents(webBundles, webBundle);
  }

  private static unpackWebEvents(webBundles: WebBundlesBuilder, lastBundle: WebBundleProto) {
    const prev = lastBundle.previous();
    if (prev) this.unpackWebEvents(webBundles, prev);
    const id = DataUnpack.unpackObjectId(lastBundle.webViewId(), lastBundle.webViewId64());
    const epoch = lastBundle.epoch();
    const bundle = webBundles.findOrCreate(id, epoch);
    bundle.addEvents(0, lastBundle);
  }

  /**
   * Unpack the string table from the bundle root.
   */
  private unpackStringTable(root) {
    const map: { [index: number]: string } = {};
    const len = root.stringTableLength();
    for (let i = 0; i < len; i++) {
      map[i + 1] = root.stringTable(i);
    }

    return map;
  }

  /**
   * Unpacks the big binary canvas blob.
   */
  private unpackCanvasBundle(root) {
    if (root.canvasBundleLength() == 0) return new Uint8Array(0);
    return new Uint8Array(root.canvasBundleArray());
  }

  /**
   * Unpack the object tables into a shader set for the entire bundle.
   */
  private unpackFrameObjectTables(frameFb: FrameRootProto) {
    const paths = this.unpackPaths(frameFb);
    const bitmaps = DataUnpack.unpackBitmaps(
      this.resourceData,
      true,
      frameFb,
      this.driver.type == PageTypeEnum.PageTypeiOS,
      this.protocolVersion,
    );
    const shaders = this.unpackShaders(frameFb);

    Object.keys(paths).forEach(id => {
      this.paths[id] = paths[id];
    });
    Object.keys(bitmaps).forEach(id => {
      this.bitmaps[id] = bitmaps[id];
    });
    Object.keys(shaders).forEach(id => {
      this.shaders[id] = shaders[id];
    });
  }

  /**
   * Assumes that 'list' has items w/ field named 'previous'. Unpacks to a list using 'fn' to map individual items.
   */
  private unpackList<T extends { previous(): T }, U>(listFb: any, fn: (t: T) => U): U[] {
    const list: U[] = [];

    while (listFb) {
      list.unshift(fn(listFb));
      listFb = listFb.previous();
    }

    return list;
  }

  /**
   * Assumes that 'list' has items w/ field named 'previous'. Walks the list, calling 'fn' for individual items.
   */
  private forEachList<T extends { previous(): T }>(listFb: T, fn: (t: T) => void) {
    while (listFb) {
      fn(listFb);
      listFb = listFb.previous();
    }
  }

  private unpackFrame(frameFb: FrameRootProto, frameBundleRoot: FrameBundleRootProto) {
    // Check the timestamp before doing anything else so that we get timestamp values even for
    // frames without an associated view.
    const ts = frameFb.frameTimestampMs();
    this.lastFrameTime = Math.max(this.lastFrameTime, ts);
    const viewRoot = frameFb.viewRoot() || frameFb.viewRoot2();
    if (!viewRoot) {
      return null;
    }

    const iOSTextLayoutCutoff = 1704085200; // midnight Jan 1, 2024, before the release of 1.45
    const shouldFudgeIOSText = frameBundleRoot.protocolVersion() < iOSTextLayoutCutoff;

    const frame = new Frame();
    frame._fudgeIOSTextBounds = shouldFudgeIOSText;
    frame.bundle = frameBundleRoot;
    frame.frame = frameFb;
    frame.fonts = {};
    frame.index = frameFb.frameIndex();
    frame.timestamp = frameFb.frameTimestampMs();
    frame.density = viewRoot.density();
    frame.idleFramesSkipped = frameFb.idleFramesSkipped();
    frame.identicalFramesSkipped = frameFb.identicalFramesSkipped();
    frame.touches = this.unpackTouches(frameFb);
    frame.topView = this.unpackView(frame, null, viewRoot.topView());
    frame.window = DataUnpack.unpackRect(viewRoot.window());
    frame.keyboardEvents = this.unpackKeyboardEvents(frameFb);
    frame.screenRootViewId64 = DataUnpack.unpackObjectId(0, frameFb.screenRootViewId64());

    // Keep track of the bitmaps that were required for this frame so we can pre-load them at view time
    frame.bitmaps = {};
    const len = frameFb.imagesLength();
    for (let i = 0; i < len; i++) {
      const imageEntryFb = frameFb.images(i);
      const id = imageEntryFb.key();

      frame.bitmaps[id] = this.bitmaps[id];
    }

    return frame;
  }

  private unpackKeyboardEvents(frameFb: FrameRootProto): KeyboardVisibilityEvent[] {
    const eventLength = frameFb.fsEventsLength();
    const kbEvents = new Array<KeyboardVisibilityEvent>();
    for (let i = 0; i < eventLength; i++) {
      const e = frameFb.fsEvents(i);
      const evtType = e.eventType();
      switch (evtType) {
        case FSEvent.EventKeyboardVisibility:
          {
            const keyboardEvent = e.event(new EventKeyboardVisibility());
            kbEvents.push(
              new KeyboardVisibilityEvent(keyboardEvent.visible(), e.sessionTimestampMs()),
            );
          }
          break;
      }
    }
    return kbEvents;
  }

  private unpackView(frame: Frame, parent: View, viewFb: ViewProto | FSFBViewProto): View {
    // Check the view cache
    let view = this.viewCache[viewFb.bb_pos];
    if (view) {
      frame.trackViewAndChildren(view);
      return view;
    }

    view = new View(viewFb.state(), this.selectorFactory);
    view.frame = frame;
    view.root = parent === null;
    view.uniqueId = DataUnpack.unpackObjectId(viewFb.uniqueId(), viewFb.uniqueId64());
    const viewStateFb: ViewStateProto = viewFb.state();

    view.canvas = this.unpackCanvas(frame, viewFb.canvas());

    // Unpack position rect/point/missing
    if (viewStateFb.position()) {
      view.position = DataUnpack.unpackRect(viewStateFb.position());
    } else if (viewStateFb.positionZero()) {
      view.position = DataUnpack.unpackPointRect(viewStateFb.positionZero());
    } else {
      view.position = Rect.ZERO();
    }

    // Relative scroll
    view.scrollF = DataUnpack.unpackPoint(viewStateFb.scrollF()) || Point.ZERO();

    // Absolute scroll (for interpolation)
    if (viewStateFb.scroll()) {
      view.scroll = DataUnpack.unpackPoint(viewStateFb.scroll()) || Point.ZERO();
    } else {
      view.scroll = DataUnpack.unpackPoint(viewStateFb.scrollShort()) || Point.ZERO();
    }

    view.alpha = parent == null ? 1 : (viewStateFb.alpha() & 0xff) / 255;
    view.hasCrop = '';
    view.textContent = '';
    view.viewStateText = '';
    view.matrix = DataUnpack.unpackMatrix(viewStateFb.matrix());
    view.flags = viewStateFb.flags1();
    view.isWebView = view.flag(this.driver.isWebViewFlag);
    view.isBlocked = view.flag(this.driver.isBlockedFlag);
    view.windowFlags = viewStateFb.windowFlags();
    view.windowType = viewStateFb.windowType();
    view.filter = this.strings[viewStateFb.filter()];
    if (view.filter && parent) {
      parent.hasComplexCompositingChild = true;
    }
    view.tintColor = DataUnpack.unpackTintColor(parent, viewStateFb.tintColor());
    view.clipmask = DataUnpack.unpackRect(viewStateFb.clipmask());

    /* Unpack children after unpacking all of this view's data so that
     * inherited properties -- such as tintColors -- can reference their
     * .parent.  Note that reprocessing the canvas requires having children
     * available, so this must happen before we do that.
     */
    if (viewFb instanceof FSFBViewProto) {
      const len = viewFb.childrenLength();
      view.children = [];
      for (let i = 0; i < len; i++) {
        view.children.push(this.unpackView(frame, view, viewFb.children(i)));
      }
    } else {
      view.children = this.unpackList(viewFb.children(), (x: ViewProto) =>
        this.unpackView(frame, view, x),
      );
    }
    const textIndex = viewFb.state().viewText();
    if (textIndex > 0) {
      view.viewStateText = this.strings[textIndex];
    }

    if (viewStateFb.childrenReversed()) {
      view.children = view.children.reverse();
    }
    this.driver.reprocessCanvas(view);

    // recursively compute (approximate) bounds for all descendants, relative to this view's top left
    // do this after the children are unpacked so we can use their bounds
    view.inclusiveBounds = new Rect(0, 0, view.position.width(), view.position.height());
    // if this view clips its children, we don't need to check them
    if (view.clipsChildrenToBounds) {
      view.hasChildOutsideBounds = false;
    } else {
      for (let child of view.children) {
        // shift to this view's coordinate system by translating by the child's top left coordinate
        let bounds = child.inclusiveBounds.translated(child.position.x1, child.position.y1);
        if (child.matrix) {
          bounds = bounds.transformedAABB(child.matrix);
        }
        view.inclusiveBounds.boundingBoxUnion(bounds);
      }
      view.hasChildOutsideBounds =
        view.inclusiveBounds.x1 < 0 ||
        view.inclusiveBounds.y1 < 0 ||
        view.inclusiveBounds.x2 > view.position.width() ||
        view.inclusiveBounds.y2 > view.position.height();
    }
    frame.trackView(view);

    this.viewCache[viewFb.bb_pos] = view;
    return view;
  }

  private unpackCanvas<OP>(frame: Frame, offset: number): Canvas<OP>[] {
    if (offset == 0) return [];

    let length = 0;
    let shift = 0;

    // eslint-disable-next-line no-var
    for (var i = 0; i < 5; i++) {
      const b = this.canvas[offset + i];

      length |= (b & 0x7f) << shift;
      shift += 7;

      // Is there more?
      if ((b & 0x80) == 0) break;
    }

    const map = (token: CanvasTokenDef, value: number) => {
      switch (token.object) {
        case CanvasTokenTypeObject.NONE:
          return value;
        case CanvasTokenTypeObject.ASSET:
          return this.resourceData.lookupAsset(value) || null;
        case CanvasTokenTypeObject.IMAGE:
          return this.bitmaps[value] || Bitmap.MISSING;
        case CanvasTokenTypeObject.PATH:
          return this.paths[value] || null;
        case CanvasTokenTypeObject.SHADER:
          return this.shaders[value] || null;
        case CanvasTokenTypeObject.STRING:
          return this.strings[value] || null;
        default:
          console.warn(`Unexpected CanvasTokenTypeObject: ${token.object}`);
          return null;
      }
    };

    try {
      // Typescript can't see that this is a valid return
      const decoder = new CanvasDecoder(this.tokenDefs, map, this.canvas, offset + i + 1, length);
      return <any>this.driver.unpackCanvas(frame, decoder);
    } catch (e) {
      // Let's not make a failure here fatal, but at least get some diagnostics
      console.log(
        'Unexpected error unpacking canvas at offset ',
        offset,
        ', length ',
        length,
        ': ',
        e,
      );
      return [];
    }
  }

  public static unpackPoint(pointFb: any): Point {
    if (!pointFb) return null;
    return new Point(pointFb.x(), pointFb.y());
  }

  public static unpackPointRect(pointFb: any): Rect {
    if (!pointFb) return null;
    return new Rect(0, 0, pointFb.x(), pointFb.y());
  }

  public static unpackPointArray(pointJson: any): Point {
    if (!pointJson) return null;
    return new Point(pointJson[0], pointJson[1]);
  }

  public static unpackRect(rectFb: RectStruct): Rect {
    if (!rectFb) return null;
    return new Rect(rectFb.x1(), rectFb.y1(), rectFb.x2(), rectFb.y2());
  }

  public static unpackRectArray(rectJson: any): Rect {
    if (!rectJson) return null;
    return new Rect(rectJson[0], rectJson[1], rectJson[2], rectJson[3]);
  }

  public static unpackMatrix(matrixFb: any): number[] {
    if (!matrixFb) return null;

    // prettier-ignore
    return [
      matrixFb.m0(), matrixFb.m1(), matrixFb.m2(),
      matrixFb.m3(), matrixFb.m4(), matrixFb.m5(),
      matrixFb.m6(), matrixFb.m7(), matrixFb.m8(),
    ];
  }

  public static unpackMatrixArray(matrixJson: any): number[] {
    if (!matrixJson) return null;

    return matrixJson;
  }

  // Unpack six numbers into an affine transformation based on PDF format conventions
  // where [ a b c d e f ] means the x-axis direction is sent to (a, b)
  public static unpackIOSMatrix6Array(matrixJson: any): number[] {
    if (!matrixJson) return null;

    const m = matrixJson;
    return [m[0], m[2], m[4], m[1], m[3], m[5], 0, 0, 1];
  }

  // Ported from
  // https://github.com/google/skia/blob/81abc43e6f0b1a789e1bf116820c8ede68d778ab/tools/pathops_sorter.htm
  static chopConic(curve: number[], dst1: number[], dst2: number[]) {
    const w = curve[6];
    const scale = 1 / (1 + w);
    const neww = Math.sqrt(0.5 + w * 0.5); // subdivide_w_value

    const m = [
      (curve[0] + 2 * w * curve[2] + curve[4]) * scale * 0.5,
      (curve[1] + 2 * w * curve[3] + curve[5]) * scale * 0.5,
    ];

    dst1[0] = curve[0];
    dst1[1] = curve[1];
    dst1[2] = (curve[0] + curve[2] * w) * scale;
    dst1[3] = (curve[1] + curve[3] * w) * scale;
    dst1[4] = m[0];
    dst1[5] = m[1];
    dst1[6] = neww;

    dst2[0] = m[0];
    dst2[1] = m[1];
    dst2[2] = (curve[2] * w + curve[4]) * scale;
    dst2[3] = (curve[3] * w + curve[5]) * scale;
    dst2[4] = curve[4];
    dst2[5] = curve[5];
    dst2[6] = neww;
  }

  // Ported from
  // https://github.com/google/skia/blob/81abc43e6f0b1a789e1bf116820c8ede68d778ab/tools/pathops_sorter.htm
  static subdivideConic(p2d: Path2D, curve: number[], level: number) {
    if (0 == level) {
      p2d.quadraticCurveTo(curve[2], curve[3], curve[4], curve[5]);
    } else {
      const part1 = [],
        part2 = [];
      DataUnpack.chopConic(curve, part1, part2);
      DataUnpack.subdivideConic(p2d, part1, level - 1);
      DataUnpack.subdivideConic(p2d, part2, level - 1);
    }
  }

  private unpackPath(path: LegacyPath): Path2D {
    const verbs = path.verbsArray();
    const points = path.pointsArray();
    const weights = path.weightsArray() || [];

    const p2d = new Path2D();
    if (!verbs || !points) {
      console.error('Path items were unexpectedly null', verbs, points, weights);
      return p2d;
    }

    let pt = 0;
    let weightIdx = 0;
    let lx = 0,
      ly = 0;
    for (let k = 0; k < verbs.length; k++) {
      switch (verbs[k]) {
        case 0:
          // moveTo()
          p2d.moveTo((lx = points[pt++]), (ly = points[pt++]));
          break;
        case 1:
          // lineTo()
          p2d.lineTo((lx = points[pt++]), (ly = points[pt++]));
          break;
        case 2:
          // quadTo()
          p2d.quadraticCurveTo(
            points[pt++],
            points[pt++],
            (lx = points[pt++]),
            (ly = points[pt++]),
          );
          break;
        case 3: {
          // conicTo()

          // control point
          const x1 = points[pt++];
          const y1 = points[pt++];
          // final point
          const x2 = points[pt++];
          const y2 = points[pt++];

          const w = weights[weightIdx++] || Math.sqrt(0.5);

          // One level of recursion seems to be sufficient
          DataUnpack.subdivideConic(p2d, [lx, ly, x1, y1, x2, y2, w], 1);

          lx = x2;
          ly = y2;
          break;
        }
        case 4:
          // cubicTo()
          p2d.bezierCurveTo(
            points[pt++],
            points[pt++],
            points[pt++],
            points[pt++],
            (lx = points[pt++]),
            (ly = points[pt++]),
          );
          break;
        case 5:
          // close()
          p2d.closePath();
          break;
      }
    }

    return p2d;
  }

  private unpackPathFromPoints(f: Float32Array): Path2D {
    const p2d = new Path2D();
    if (!f) {
      // This has been seen to be null in some cases
      return p2d;
    }
    let idx = 0;
    for (let k = 0; k < f.length; k += 3) {
      const fraction = f[k];
      const x = f[k + 1];
      const y = f[k + 2];

      if (idx == fraction) {
        p2d.moveTo(x, y);
      } else {
        p2d.lineTo(x, y);
      }
      idx = fraction;
    }
    return p2d;
  }

  private unpackPaths(frameFb: FrameRootProto) {
    const paths: { [imageId: number]: Path2D } = {};

    const len = frameFb.pathsLength();
    for (let i = 0; i < len; i++) {
      const pathEntryFb = frameFb.paths(i);
      const id = pathEntryFb.key();
      const legacyPath = pathEntryFb.legacyPath();
      if (legacyPath) {
        paths[id] = this.unpackPath(legacyPath);
      } else {
        paths[id] = this.unpackPathFromPoints(pathEntryFb.valueArray());
      }
    }

    return paths;
  }

  private unpackShaders(frameFb: any) {
    const shaders: { [id: number]: CanvasShader } = {};

    const len = frameFb.shadersLength();
    for (let i = 0; i < len; i++) {
      const shaderEntryFb = frameFb.shaders(i);
      const shaderFb = shaderEntryFb.value();

      // if the shader has a null value, then we didn't encode it while recording
      if (shaderFb == null) {
        continue;
      }

      const id = shaderEntryFb.key();

      const shader = new CanvasShader();
      shader.type = shaderFb.typ();
      shader.p1 = DataUnpack.unpackPoint(shaderFb.p1());
      shader.p2 = DataUnpack.unpackPoint(shaderFb.p2());
      shader.r = shaderFb.r();

      shader.positions = [];
      const posLen = shaderFb.positionsLength();
      for (let j = 0; j < posLen; j++) {
        let f = shaderFb.positions(j);
        // Clamp to 0..1
        if (!Number.isFinite(f) || f > 1) {
          f = 1;
        } else if (f < 0) {
          f = 0;
        }
        shader.positions.push(f);
      }
      shader.colors = [];
      const colorsLen = shaderFb.colorsLength();
      for (let j = 0; j < colorsLen; j++)
        shader.colors.push(DataUnpack.unpackCssColor(shaderFb.colors(j)));
      shader.tileMode = DataUnpack.unpackTileMode(shaderFb.tileMode());
      shaders[id] = shader;
    }

    return shaders;
  }

  private static unpackBitmaps(
    resourceData: Resources,
    attachImage: boolean,
    frameRoot: any,
    isIOS: boolean,
    protocolVersion: number,
  ): { [imageId: number]: Bitmap } {
    const bitmaps: { [imageId: number]: Bitmap } = {};

    const len = frameRoot.imagesLength();
    for (let i = 0; i < len; i++) {
      const imageEntryFb = frameRoot.images(i);
      if (!imageEntryFb) {
        // defensive
        continue;
      }
      const id = imageEntryFb.key();
      const bitmap = DataUnpack.unpackBitmap(resourceData, imageEntryFb, isIOS, protocolVersion);
      if (bitmap) {
        if (attachImage && bitmap.url) {
          bitmap.img = new Image();
          bitmap.loaded = loadEvent(bitmap.img, () => {
            bitmap.img.src = bitmap.url;
          });
          // unfortunately, failed load events don't give us a reason
          bitmap.loaded.catch(() => {
            fetch(bitmap.url)
              .then(function (response) {
                return response.blob();
              })
              .then(function (blob) {
                // Check to see if the blob is a PNG with the wrong type
                // Xcode transparently rasterizes SVG assets to PNG - somewhere, we are tagging the
                // reencoded PNG as a SVG. (MOCA-9159)
                // Reading the contents is expensive but even an invalid PNG should be at least 50 bytes:
                // https://github.com/mathiasbynens/small/blob/master/png-truncated.png
                if (blob.type == 'image/svg+xml' && blob.size > 50) {
                  blob = new Blob([blob], { type: 'image/png' });
                  bitmap.img = new Image();
                  bitmap.loaded = loadEvent(bitmap.img, () => {
                    if (!DataUnpack.retaggedBlobURLs.has(bitmap.url)) {
                      DataUnpack.retaggedBlobURLs.set(
                        bitmap.url,
                        URL.createObjectURL(new Blob([blob], { type: 'image/png' })),
                      );
                    }
                    let blobURL = DataUnpack.retaggedBlobURLs.get(bitmap.url);
                    bitmap.img.src = blobURL;
                  });
                  bitmap.loaded.catch(() => {
                    // The SVG is not a PNG after all
                    bitmap.loadFailed = true;
                  });
                } else {
                  bitmap.loadFailed = true;
                }
              });
          });
        }
        bitmaps[id] = bitmap;
      }
    }

    return bitmaps;
  }

  private static unpackBitmap(
    resourceData: Resources,
    imageEntryFb: any,
    isIOS: boolean,
    protocolVersion: number,
  ): Bitmap {
    const imageFb = imageEntryFb.value();
    if (!imageFb) {
      return null;
    }

    const bitmap = new Bitmap();
    bitmap.width = imageFb.width();
    bitmap.height = imageFb.height();
    bitmap.density = imageFb.density();

    // Map the flatbuffer type
    switch (imageFb.imageType()) {
      case FSFBImageType.STANDARD:
        bitmap.type = BitmapType.Standard;
        break;
      case FSFBImageType.MASKED:
        bitmap.type = BitmapType.Masked;
        break;
      case FSFBImageType.RASTERIZED_VECTOR:
        bitmap.type = BitmapType.RasterizedVector;
        break;
      default: {
        console.log('Invalid image type!');
        bitmap.type = BitmapType.Standard;
      }
    }

    const dominantColor = imageFb.dominantColor();
    bitmap.dominantColor = this.unpackCssColor(dominantColor);

    const asset = imageFb.asset();
    if (asset) {
      bitmap.asset = asset;
      const assetData = resourceData.lookupImage(asset);
      if (assetData) {
        bitmap.url = resourceData.getPathForResourceByHash(assetData.getName(), true);
        bitmap.ninePatch = assetData.getNinePatchChunk();
      }
      bitmap.params = resourceData.lookupParams(asset);
    }

    /* Some things that look like they could conceivably be asset-derived
     * don't have URLs (they could have been "asset-like" inasmuch as they
     * have derivations, but they don't actually derive from anything that
     * we uploaded).  So for those things, we take a look at the upload ID,
     * instead.
     *
     * XXX: rename "asset" to "transformations" in the flatbuffer, perhaps,
     * and have a fixed "root image" type in the flatbuffer that refers
     * either to an asset or an upload-ID?
     */
    if (!bitmap.url) {
      const hash = imageFb.uploadId();
      if (bitmap.type != BitmapType.Masked && hash) {
        bitmap.url = resourceData.getPathForResourceByHash(hash, false);
        const ninePatch: number[] = [];
        const ninePatchLength = imageFb.ninePatchLength();
        for (let j = 0; j < ninePatchLength; j++) {
          ninePatch.push(imageFb.ninePatch(j));
        }

        if (ninePatch.length > 0) {
          bitmap.ninePatch = NinePatchChunkParser.parse(ninePatch, false);
        }
      }
    }

    return bitmap;
  }

  private unpackTouches(frameRoot: any) {
    const touches = [];

    const len = frameRoot.touchesLength();
    for (let i = 0; i < len; i++) {
      const touchFb = this.unpackTouch(frameRoot.touches(i));
      if (touchFb) touches.push(touchFb);
    }

    return touches;
  }

  private unpackTouch(touchFb: any) {
    const len = touchFb.dataLength();
    if (len == 0) return null;

    const id = DataUnpack.unpackObjectId(touchFb.id(), touchFb.id64());

    // One position, fake spline
    if (len == 2) {
      return new Touches(
        id,
        {
          p1: { x: touchFb.data(0), y: touchFb.data(1) },
          p2: { x: touchFb.data(0), y: touchFb.data(1) },
          t1: { x: 0, y: 0 },
          t2: { x: 0, y: 0 },
        },
        touchFb.isNew(),
        touchFb.isDown(),
      );
    }

    // TODO(mmastrac): Assumes touches are evenly spaced -- need to track timestamps here
    const speed = 5;

    // Real spline
    return new Touches(
      id,
      {
        p1: { x: touchFb.data(0), y: touchFb.data(1) },
        p2: { x: touchFb.data(4), y: touchFb.data(5) },
        t1: { x: touchFb.data(2) * speed, y: touchFb.data(3) * speed },
        t2: { x: touchFb.data(6) * speed, y: touchFb.data(7) * speed },
      },
      touchFb.isNew(),
      touchFb.isDown(),
    );
  }

  public static unpackTileMode(mode: number): number {
    mode = mode || 0;
    if (mode == 0) return null;

    return mode;
  }

  public static unpackPorterDuffMode(mode: number): number {
    return mode || null;
  }

  /**
   * argb -> CSS color.
   */
  public static unpackCssColor(color: number): string {
    if (color == 0) return 'transparent';

    color = color >>> 0;
    if (color >>> 24 == 0xff) {
      return `#${`00000${color.toString(16)}`.slice(-6)}`;
    } else {
      const b = color & 0xff;
      color >>>= 8;
      const g = color & 0xff;
      color >>>= 8;
      const r = color & 0xff;
      color >>>= 8;
      const a = color & 0xff;

      // We don't need all that precision
      const alpha = ~~((a / 255) * 100) / 100;
      return `rgba(${r},${g},${b},${alpha})`;
    }
  }

  public static unpackTintColor(parent: View, color: number): string {
    // Unspecified color means take it from the parent view
    if (color == 0) {
      if (parent && parent.tintColor) {
        return parent.tintColor;
      } else {
        return 'black';
      }
    }
    return this.unpackCssColor(color);
  }

  public static unpackObjectId(id: number, id64): ObjectId {
    // flatbuffers used to use its own Long type for 64-bit numbers
    // consisting of the `high` 32 bits and `low` 32 bits,
    // but they have since switched to using the standard BigInt type.
    // This function will support either type of value for id64.
    if (id64) {
      // Handle non-BigInt vs BigInt case
      if (typeof id64 === 'object') {
        // If this is a number that only fits in 64 bits, return the hex equivalent of the 64-bit string.
        // padStart isn't supported by TypeScript yet, but it's widely available.
        if (id64.high) {
          return (
            (id64.high >>> 0).toString(16) + (<any>(id64.low >>> 0).toString(16)).padStart(8, '0')
          );
        }

        // Otherwise just return low as if it were id
        if (id64.low) {
          return (id64.low >>> 0).toString(16);
        }
      } else if (id64 >= 2 ** 32) {
        // If this is a number that only fits in 64 bits, return the hex equivalent of the 64-bit string.
        // padStart isn't supported by TypeScript yet, but it's widely available.
        return id64.toString(16).padStart(8, '0');
      } else {
        // Otherwise just return id64 as if it were a 32-bit id
        return id64.toString(16);
      }
    }

    // Otherwise we fall back to id
    return (id >>> 0).toString(16);
  }
}
